import { useCallback } from 'react'
import { useFeatureFlags } from '@npco/mp-utils-mp-feature-flags'
import {
  Box,
  COLOR,
  Flex,
  SvgIcon,
  TileSelector,
} from '@npco/zeller-design-system'

import T1ShellBlack from 'assets/images/t1-shell-black.png'
import T1ShellWhite from 'assets/images/t1-shell-white.png'
import T2ShellBlack from 'assets/images/t2-shell-black.png'
import T2ShellGrey from 'assets/images/t2-shell-grey.png'
import T2ShellWhite from 'assets/images/t2-shell-white.png'
import { ReactComponent as TerminalIcon } from 'assets/svg/payment-source/terminal.svg'
import { ReactComponent as T1MarkIcon } from 'assets/svg/t1-mark.svg'
import { ReactComponent as T2Icon } from 'assets/svg/t2.svg'
import { ReactComponent as T2MarkIcon } from 'assets/svg/t2-mark.svg'

import * as styled from '../TerminalPreview.styled'
import { TerminalModel } from '../TerminalPreview.types'

interface ControlPanelProps {
  isInModal?: boolean
  terminalImageUrl: string
  terminalModel: TerminalModel.T1 | TerminalModel.T2
  setTerminalImageUrl: React.Dispatch<React.SetStateAction<string>>
  setTerminalModel: React.Dispatch<React.SetStateAction<TerminalModel>>
}

export const ControlPanel = ({
  isInModal = false,
  terminalImageUrl,
  terminalModel,
  setTerminalImageUrl,
  setTerminalModel,
}: ControlPanelProps) => {
  const flags = useFeatureFlags()

  const handleT2TileClick = useCallback(() => {
    if (terminalImageUrl === T1ShellBlack) {
      setTerminalImageUrl(T2ShellBlack)
    } else if (terminalImageUrl === T1ShellWhite) {
      setTerminalImageUrl(T2ShellWhite)
    }
    setTerminalModel(TerminalModel.T2)
  }, [setTerminalImageUrl, setTerminalModel, terminalImageUrl])

  const handleT1TileClick = useCallback(() => {
    if (terminalImageUrl === T2ShellGrey) {
      setTerminalImageUrl(T1ShellBlack)
    } else if (terminalImageUrl === T2ShellBlack) {
      setTerminalImageUrl(T1ShellBlack)
    } else if (terminalImageUrl === T2ShellWhite) {
      setTerminalImageUrl(T1ShellWhite)
    }
    setTerminalModel(TerminalModel.T1)
  }, [setTerminalImageUrl, setTerminalModel, terminalImageUrl])

  const handleBlackTileClick = useCallback(() => {
    if (terminalModel === TerminalModel.T1) {
      setTerminalImageUrl(T1ShellBlack)
    } else {
      setTerminalImageUrl(T2ShellBlack)
    }
  }, [setTerminalImageUrl, terminalModel])

  const handleWhiteTileClick = useCallback(() => {
    if (terminalModel === TerminalModel.T1) {
      setTerminalImageUrl(T1ShellWhite)
    } else {
      setTerminalImageUrl(T2ShellWhite)
    }
  }, [setTerminalImageUrl, terminalModel])

  const handleGreyTileClick = useCallback(() => {
    setTerminalModel(TerminalModel.T2)
    setTerminalImageUrl(T2ShellGrey)
  }, [setTerminalImageUrl, setTerminalModel])

  return (
    <Flex
      flexDirection="column"
      pt={isInModal ? '0px' : '5px'}
      gap={isInModal ? '24px' : '64px'}
    >
      <Flex
        width={isInModal ? '168px' : 'auto'}
        pl={isInModal ? '16px' : '0px'}
        flexDirection={isInModal ? 'row' : 'column'}
        gap="12px"
        justifyContent="center"
      >
        {flags.T2Uplift && (
          <>
            <TileSelector
              name="t2-model"
              content={
                <styled.LogoWrapper>
                  <SvgIcon>
                    <T2Icon />
                  </SvgIcon>
                  <Box position="absolute" top="35px" left="32px" as="figure">
                    <SvgIcon width="12px" height="9px">
                      <T2MarkIcon />
                    </SvgIcon>
                  </Box>
                </styled.LogoWrapper>
              }
              selected={terminalModel === TerminalModel.T2}
              onChange={handleT2TileClick}
              value="0"
            />
            <TileSelector
              name="t1-model"
              content={
                <styled.LogoWrapper>
                  <SvgIcon color={COLOR.GREY_700}>
                    <TerminalIcon />
                  </SvgIcon>
                  <Box position="absolute" top="35px" left="32px" as="figure">
                    <SvgIcon width="12px" height="9px">
                      <T1MarkIcon />
                    </SvgIcon>
                  </Box>
                </styled.LogoWrapper>
              }
              selected={terminalModel === TerminalModel.T1}
              onChange={handleT1TileClick}
              value="1"
            />
          </>
        )}
      </Flex>
      <Flex
        width={isInModal ? '168px' : 'auto'}
        flexDirection={isInModal ? 'row' : 'column'}
        pl={isInModal ? '16px' : '0px'}
        gap="12px"
        justifyContent="center"
      >
        <TileSelector
          name="default-terminal-black"
          content={<styled.BlackTile />}
          selected={
            terminalModel === TerminalModel.T1
              ? terminalImageUrl === T1ShellBlack
              : terminalImageUrl === T2ShellBlack
          }
          onChange={handleBlackTileClick}
          value="2"
        />
        <TileSelector
          name="default-terminal-white"
          content={<styled.WhiteTile />}
          selected={
            terminalModel === TerminalModel.T1
              ? terminalImageUrl === T1ShellWhite
              : terminalImageUrl === T2ShellWhite
          }
          onChange={handleWhiteTileClick}
          value="3"
        />
        {terminalModel === TerminalModel.T2 && (
          <TileSelector
            name="default-terminal-grey"
            content={<styled.GreyTile />}
            selected={terminalImageUrl === T2ShellGrey}
            onChange={handleGreyTileClick}
            value="4"
          />
        )}
      </Flex>
    </Flex>
  )
}
